import { MODEL_UI } from '../../copy/model-configuration'
import { t } from '../../copy'
import { ChevronDown, CirclePause, ExternalLink, RotateCcw, SlidersHorizontal } from 'lucide-react'
import { useState } from 'react'

import { useAccountMembership, useProjects } from '../../app/queries'
import { ApiFailure } from '../../app/errors'
import { AITeamNav } from '../ai-team-nav'
import { Button } from '../../components/button'
import { Card, CardHint, CardTitle } from '../../components/card'
import { Modal } from '../../components/modal'
import { Page, PageHeader } from '../../components/page'
import { ScreenError, ScreenLoading } from '../../components/state'
import { Tag, type TagTone } from '../../components/tag'
import { useModelProfiles, type ModelProfile } from '../models/model-library'
import { ModelSourcePicker, type BindableModel } from '../models/model-source-picker'
import {
  useDeleteSystemModelOverride,
  usePauseSystemModel,
  useSetSystemModel,
  useSystemModels,
  type SystemModelModuleView,
} from './queries'

const fieldClass =
  'min-h-11 w-full rounded-npc-button border border-npc-border bg-npc-panel px-npc-gap-lg text-npc-body text-npc-ink outline-none focus:border-npc-green disabled:cursor-not-allowed disabled:bg-npc-surface disabled:text-npc-ink-softer'

export function SystemModelsScreen() {
  const [projectId, setProjectId] = useState(
    () => new URLSearchParams(window.location.search).get('project_id') ?? '',
  )
  const projects = useProjects()
  const membership = useAccountMembership()
  const modules = useSystemModels(projectId || undefined)
  const profiles = useModelProfiles(projectId || undefined)

  if (modules.isPending || profiles.isPending || membership.isPending)
    return (
      <ScreenLoading
        shape="list"
        width="workspace"
        className="ai-team-theme min-h-full bg-m-bg0 text-npc-ink"
      />
    )
  if (modules.isError) return <ScreenError error={modules.error} />
  if (profiles.isError) return <ScreenError error={profiles.error} />

  const capabilities = membership.data?.capabilities ?? []
  const canWrite = projectId
    ? capabilities.includes('project.manage')
    : capabilities.includes('vendor.manage')

  // Platform-seeded models often cannot 测试连接 (keys stay in orchestrator).
  // Requiring verified here empties every dropdown, including Lulu.
  const available = profiles.data.profiles.filter(
    (profile) => profile.enabled && profile.status === 'available',
  )
  const projectOptions = projects.data?.items ?? []

  return (
    <Page className="ai-team-theme min-h-full bg-m-bg0 text-npc-ink" width="workspace">
      <AITeamNav current="/system-models" />
      <PageHeader
        title={MODEL_UI.ui_002}
        subtitle={MODEL_UI.ui_232}
        actions={
          <label className="grid min-w-52 gap-npc-gap-xs text-npc-meta text-npc-ink-soft max-[600px]:min-w-0">
            <span>{MODEL_UI.ui_233}</span>
            <select
              value={projectId}
              onChange={(event) => setProjectId(event.target.value)}
              className={fieldClass}
              aria-label={MODEL_UI.ui_234}
            >
              <option value="">{MODEL_UI.ui_235}</option>
              {projectOptions.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name} {MODEL_UI.ui_236}
                </option>
              ))}
            </select>
          </label>
        }
      />

      <div className="mt-npc-grid-lg border-y border-npc-border bg-npc-surface px-npc-card py-npc-grid-sm text-npc-caption text-npc-ink-medium">
        {MODEL_UI.ui_237}
      </div>

      {!canWrite ? (
        <div className="mt-npc-grid-sm border-y border-npc-border bg-npc-surface px-npc-card py-npc-grid-sm text-npc-caption text-npc-ink-medium">
          {MODEL_UI.ui_238}
        </div>
      ) : null}

      {available.length === 0 ? (
        <div className="mt-npc-grid-lg border-y border-npc-border py-npc-section text-center">
          <SlidersHorizontal size={24} aria-hidden="true" className="mx-auto text-npc-ink-softer" />
          <h2 className="mb-0 mt-npc-grid-sm text-npc-section font-semibold">{MODEL_UI.ui_239}</h2>
          <p className="mx-auto mb-0 mt-npc-gap-xs max-w-xl text-npc-caption text-npc-ink-soft">
            {MODEL_UI.ui_240}
          </p>
          <a
            href={`/models?return_to=${encodeURIComponent('/system-models')}`}
            className="mt-npc-grid inline-flex items-center gap-npc-gap-xs text-npc-caption font-semibold text-npc-green-deep hover:underline"
          >
            {MODEL_UI.ui_241}
            <ExternalLink size={14} aria-hidden="true" />
          </a>
        </div>
      ) : null}

      <div className="mt-npc-grid-lg grid grid-cols-1 gap-npc-grid-sm">
        {modules.data.modules.map((view) => (
          <ModuleRow
            key={view.module.key}
            view={view}
            profiles={available}
            projectId={projectId || undefined}
            canWrite={canWrite}
            onManageModels={(providerId) => {
              const returnPath = projectId
                ? `/system-models?project_id=${encodeURIComponent(projectId)}`
                : '/system-models'
              const params = new URLSearchParams({ return_to: returnPath })
              if (providerId) params.set('add_provider', providerId)
              const target = `/models?${params.toString()}`
              window.history.pushState({}, '', target)
              window.dispatchEvent(new PopStateEvent('popstate'))
            }}
          />
        ))}
      </div>
    </Page>
  )
}

function ModuleRow({
  view,
  profiles,
  projectId,
  canWrite,
  onManageModels,
}: {
  view: SystemModelModuleView
  profiles: readonly ModelProfile[]
  projectId?: string
  canWrite: boolean
  onManageModels: (providerId?: string) => void
}) {
  const [pickerOpen, setPickerOpen] = useState(false)
  const setModel = useSetSystemModel()
  const pause = usePauseSystemModel()
  const removeOverride = useDeleteSystemModelOverride()
  const currentID = view.effective?.profile?.id ?? ''
  const busy = setModel.isPending || pause.isPending || removeOverride.isPending
  const disabled = !view.module.supports_binding
  const fixedRuntime = Boolean(view.module.runtime_provider && view.module.runtime_model)
  const options: BindableModel[] = profiles.map((profile) => ({
    profileId: profile.id,
    displayName: profile.display_name,
    modelId: profile.model_id,
    provider: profile.provider,
    connectionId: profile.connection_id,
    connectionName: profile.connection_name,
  }))

  return (
    <Card
      className="grid min-w-0 grid-cols-[minmax(0,1fr)_minmax(280px,420px)] items-center gap-npc-card border-x-0 shadow-none max-[760px]:grid-cols-1"
      data-testid={`system-module-${view.module.key}`}
    >
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-npc-gap-sm">
          <CardTitle>{view.module.name}</CardTitle>
          <Tag tone={stateTone(view.state)}>{stateLabel(view.state)}</Tag>
          {view.project ? (
            <Tag tone="neutral">{MODEL_UI.ui_242}</Tag>
          ) : view.global ? (
            <Tag tone="neutral">{MODEL_UI.ui_235}</Tag>
          ) : null}
        </div>
        <p className="mb-0 mt-npc-gap-xs text-npc-caption text-npc-ink-medium">
          {view.module.description}
        </p>
        <CardHint className="mt-npc-gap-xs">{view.module.implementation_note}</CardHint>
        {view.state === 'needs_replacement' ? (
          <p role="alert" className="mb-0 mt-npc-gap-xs text-npc-caption text-npc-red-ink">
            {MODEL_UI.ui_243}
          </p>
        ) : null}
      </div>
      <div className="grid min-w-0 gap-npc-gap-sm">
        {fixedRuntime ? (
          <div
            className={`${fieldClass} flex items-center justify-between gap-npc-gap text-left`}
            data-testid={`system-module-${view.module.key}-runtime`}
          >
            <span className="min-w-0">
              <span className="block truncate font-semibold">{view.module.runtime_model}</span>
              <span className="block truncate text-npc-meta text-npc-ink-soft">
                {view.module.runtime_provider}/{view.module.runtime_model}
              </span>
            </span>
            <Tag tone="ok">{MODEL_UI.ui_249}</Tag>
          </div>
        ) : (
          <button
            type="button"
            disabled={!canWrite || disabled || busy}
            onClick={() => setPickerOpen(true)}
            className={`${fieldClass} flex items-center justify-between gap-npc-gap text-left`}
            aria-label={t('model_configuration.module_default_model_aria', {
              module: view.module.name,
            })}
          >
            <span className="min-w-0">
              <span className="flex min-w-0 flex-wrap items-center gap-npc-gap-xs">
                <span className="truncate font-semibold">
                  {view.effective?.profile?.display_name ??
                    (disabled ? MODEL_UI.ui_244 : MODEL_UI.ui_245)}
                </span>
                {view.effective?.profile ? (
                  <Tag tone="neutral">{view.effective.profile.connection_name}</Tag>
                ) : null}
              </span>
              {view.effective?.profile ? (
                <span className="block truncate text-npc-meta text-npc-ink-soft">
                  {view.effective.profile.provider}/{view.effective.profile.model_id}
                </span>
              ) : null}
            </span>
            <ChevronDown size={16} aria-hidden="true" className="shrink-0" />
          </button>
        )}
        <div className="flex flex-wrap justify-end gap-npc-gap-sm">
          {canWrite && !disabled ? (
            <Button
              size="sm"
              variant="ghost"
              disabled={busy}
              onClick={() => pause.mutate({ moduleKey: view.module.key, projectId })}
            >
              <CirclePause size={14} aria-hidden="true" />
              {MODEL_UI.ui_246}
            </Button>
          ) : null}
          {canWrite && projectId && view.project ? (
            <Button
              size="sm"
              variant="ghost"
              disabled={busy}
              onClick={() => removeOverride.mutate({ moduleKey: view.module.key, projectId })}
            >
              <RotateCcw size={14} aria-hidden="true" />
              {MODEL_UI.ui_247}
            </Button>
          ) : null}
        </div>
        {setModel.isError || pause.isError || removeOverride.isError ? (
          <p role="alert" className="m-0 text-npc-caption text-npc-red-ink">
            {savedFailureText(setModel.error ?? pause.error ?? removeOverride.error)}
          </p>
        ) : null}
      </div>
      {pickerOpen ? (
        <Modal
          open
          onOpenChange={setPickerOpen}
          title={t('model_configuration.module_default_model_aria', {
            module: view.module.name,
          })}
          description={t('model_configuration.binding_picker_catalog_hint')}
          className="ai-team-theme"
        >
          <ModelSourcePicker
            options={options}
            value={currentID}
            scope={projectId ? 'project' : 'account'}
            projectId={projectId}
            disabled={busy}
            onSelect={(profileId) => {
              setModel.mutate(
                { moduleKey: view.module.key, profileId, projectId },
                { onSuccess: () => setPickerOpen(false) },
              )
            }}
            onManageModels={onManageModels}
          />
        </Modal>
      ) : null}
    </Card>
  )
}

/**
 * 存失败时说哪一句。
 *
 * **后端按契约给了句子就用它的。** 那句是它按模板渲染好的，比这边猜得准——
 * 这条规矩 app/errors.ts 里已经写着，只是这一屏之前没照做：无论后端说什么，
 * 它一律显示同一句「请检查模型状态后重试」。
 *
 * 代价是真的：lulu_reasoning 接线之后，「模型不可用」（去把 Key 或套餐弄好）
 * 与「Lulu 用不了这个模型」（换一个模型）是**两件下一个动作不同的事**，
 * 服务端已经分成两个错误码，而界面把它们又合回了一句。
 * 照那句话去做的人，会去查一个根本没坏的 Key。
 *
 * 拿不到后端那句话时（网络断了、路由不存在）才回落到本地这句。
 */
function savedFailureText(error: unknown): string {
  if (error instanceof ApiFailure && error.kind === 'reported' && error.message.trim() !== '') {
    return error.message
  }
  return MODEL_UI.ui_248
}

function stateLabel(state: SystemModelModuleView['state']): string {
  const labels: Record<SystemModelModuleView['state'], string> = {
    active: MODEL_UI.ui_249,
    paused: MODEL_UI.ui_250,
    needs_replacement: MODEL_UI.ui_251,
    not_wired: MODEL_UI.ui_252,
    not_enabled: MODEL_UI.ui_244,
    unconfigured: MODEL_UI.ui_253,
  }
  return labels[state]
}

function stateTone(state: SystemModelModuleView['state']): TagTone {
  if (state === 'active') return 'ok'
  if (state === 'needs_replacement') return 'danger'
  if (state === 'not_wired' || state === 'unconfigured') return 'warn'
  return 'neutral'
}
